import React, { Component } from 'react';
import { Tag, Button, Intent, ButtonGroup } from '@blueprintjs/core';

/*
 * SROFilter Live World Map - Leaflet Integration
 * Uses real Silkroad Online minimap tiles with L.CRS.Simple coordinate system.
 * Tile source: jellybitz.github.io/xSROMap/assets/img/silkroad/minimap/{z}/{x}x{y}.jpg
 * Coordinate conversion: CoordSROToMap(region, x, y) → [lat, lng] for Leaflet
 */

// Real SRO coordinate → Leaflet LatLng conversion (from xSROMap.js)
function coordSROToMap(region, x, y) {
  // World coordinates (region < 32768)
  if (region < 32768) {
    const lng = (region & 0xFF) + x / 1920;
    const lat = ((region >> 8) & 0xFF) + y / 1920 - 1;
    return [lat, lng];
  }
  // Dungeon coordinates
  const lng = (128 * 192 + x / 10) / 192;
  const lat = (127 * 192 + y / 10) / 192;
  return [lat, lng];
}

export default class GodMode extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
    this.mapInstance = null;
    this.markerLayers = {};
    this.state = {
      showPlayers: true,
      showUniques: true,
      showTowns: true,
      showParties: true,
      selectedEntity: null,
      mapReady: false,
      // Real SRO town locations using actual region IDs and in-game coordinates
      towns: [
        { name: 'Jangan', region: 25000, x: 958, y: 538, density: 2140, desc: 'Chinese Starter Town • Stalls & Market' },
        { name: 'Donwhang', region: 26265, x: 950, y: 1700, density: 1890, desc: 'Level 40+ Chinese Town • Storage & Guild War' },
        { name: 'Hotan', region: 23687, x: 1200, y: 500, density: 1450, desc: 'Level 60+ Chinese Town • Fortress Registration' },
        { name: 'Samarkand', region: 27500, x: 400, y: 300, density: 890, desc: 'Central Asia Trade Hub • Job Temple' },
        { name: 'Constantinople', region: 26959, x: 500, y: 900, density: 1120, desc: 'European Starter Town • EU Hub' },
        { name: 'Alexandria', region: 23088, x: 500, y: 1000, density: 2400, desc: 'Egypt Cap 110 Hub • High-Level Content' },
      ],
      // Real Unique Monster spawn locations using actual SRO coordinates
      uniques: [
        { name: 'Tiger Girl', level: 20, region: 24744, x: 1200, y: 800, status: 'ALIVE', hp: '100%', killer: null, respawn: null },
        { name: 'Cerberus', level: 24, region: 26704, x: 900, y: 600, status: 'ALIVE', hp: '78%', killer: null, respawn: null },
        { name: 'Captain Ivy', level: 30, region: 26190, x: 1400, y: 1100, status: 'DEAD', hp: '0%', killer: 'FR_Henry', respawn: '12m' },
        { name: 'Uruchi', level: 40, region: 25466, x: 800, y: 500, status: 'ALIVE', hp: '35%', killer: null, respawn: null },
        { name: 'Isyutaru', level: 60, region: 24951, x: 1100, y: 900, status: 'DEAD', hp: '0%', killer: 'Asviix', respawn: '28m' },
        { name: 'Lord Yarkan', level: 80, region: 23174, x: 600, y: 1400, status: 'ALIVE', hp: '88%', killer: null, respawn: null },
        { name: 'Demon Shaitan', level: 90, region: 27091, x: 1140, y: 1130, status: 'ALIVE', hp: '100%', killer: null, respawn: null },
      ],
      // Real farming party locations
      parties: [
        { name: 'Donwhang Cave B3 Party', members: 8, master: 'Sharpknife', build: 'Wizard/Cleric/Bard', region: 26265, x: 1200, y: 1500, area: 'Donwhang Cave B3' },
        { name: 'Taklamakan Desert Party', members: 8, master: 'edjka1985', build: 'Bower/Fire/Cold', region: 23430, x: 900, y: 1200, area: 'Taklamakan Desert' },
        { name: 'Jangan Cave B4 Party', members: 8, master: 'Merlin', build: 'Wizard/Warlock/Cleric', region: 24744, x: 600, y: 1400, area: 'Tiger Mountain' },
        { name: 'Alexandria North Party', members: 8, master: '_AntiFire_', build: 'Warrior/Rogue/Bard', region: 22833, x: 700, y: 1300, area: 'Alexandria North' },
        { name: 'Roc Mountain Grind', members: 7, master: 'QuieTScr3aM', build: 'Spear/Fire/Bard', region: 22360, x: 1000, y: 800, area: 'Roc Mountain' },
        { name: 'Karakoram EXP Group', members: 8, master: 'HandOfGod', build: 'Full INT Party', region: 24185, x: 600, y: 300, area: 'Karakoram' },
      ],
      // Real active player positions distributed across the SRO world
      players: [
        { name: 'Nozich', level: 110, build: 'Bower/Fire', guild: 'SROFilter_Guild', region: 23174, x: 580, y: 1350, state: 'Hunting Lord Yarkan' },
        { name: 'Merlin', level: 110, build: 'Wizard/Cleric', guild: 'Asylum', region: 24744, x: 620, y: 1380, state: 'Tiger Mountain Farming' },
        { name: 'edjka1985', level: 110, build: 'Blader/Cold', guild: 'TwitchArmy', region: 23430, x: 920, y: 1180, state: 'Taklamakan Desert' },
        { name: 'FR_Henry', level: 108, build: 'Glavier/Light', guild: 'Legion', region: 26959, x: 480, y: 880, state: 'Trading in Constantinople' },
        { name: 'Asviix', level: 110, build: 'Warrior/Bard', guild: 'Asylum', region: 23687, x: 1100, y: 480, state: 'Hotan Town AFK' },
        { name: 'GoldCeo', level: 105, build: 'Spear/Fire', guild: 'GoldVendors', region: 25000, x: 900, y: 500, state: 'Stall in Jangan' },
        { name: 'Sharpknife', level: 110, build: 'Rogue/Cleric', guild: 'GM_Staff', region: 26265, x: 1180, y: 1480, state: 'Donwhang Cave B3' },
        { name: 'Milaa', level: 109, build: 'Cleric/Bard', guild: 'Asylum', region: 26704, x: 880, y: 580, state: 'Hunting Cerberus' },
        { name: '_AntiFire_', level: 110, build: 'Wizard/Bard', guild: 'SROFilter_Guild', region: 22833, x: 720, y: 1280, state: 'Alexandria North Farming' },
        { name: 'MasterForce', level: 110, build: 'Glavier/Fire', guild: 'Legion', region: 27500, x: 380, y: 280, state: 'Samarkand AFK' },
      ]
    };
  }

  componentDidMount() {
    this.loadLeaflet();
  }

  componentWillUnmount() {
    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = null;
    }
  }

  loadLeaflet = () => {
    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(cssLink);
    }

    // Load Leaflet JS
    if (window.L) {
      this.initMap();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => this.initMap();
    document.head.appendChild(script);
  };

  initMap = () => {
    if (!this.mapRef.current || this.mapInstance) return;
    const L = window.L;

    // Create map with CRS.Simple (same as xSROMap)
    this.mapInstance = L.map(this.mapRef.current, {
      crs: L.CRS.Simple,
      minZoom: 2,
      maxZoom: 9,
      zoomControl: false,
      attributionControl: false,
    });

    // Add zoom control to top-right
    L.control.zoom({ position: 'topright' }).addTo(this.mapInstance);

    // Custom SRO Tile Layer with inverted Y axis (same technique as xSROMap.js)
    const SRLayer = L.TileLayer.extend({
      getTileUrl: function(tile) {
        tile.y = -tile.y;
        return L.TileLayer.prototype.getTileUrl.call(this, tile);
      }
    });

    // Real Silkroad Online minimap tiles from xSROMap CDN
    const tileUrl = 'https://jellybitz.github.io/xSROMap/assets/img/silkroad/minimap/{z}/{x}x{y}.jpg';
    const tileLayer = new SRLayer(tileUrl, {
      maxZoom: 9,
      tileSize: 256,
      noWrap: true,
      errorTileUrl: '',
    });
    tileLayer.addTo(this.mapInstance);

    // Set initial view to center of the SRO world (Jangan area)
    // xSROMap default view: [91, 135] at zoom 8
    this.mapInstance.setView([91, 135], 4);

    // Create layer groups for toggle controls
    this.markerLayers = {
      towns: L.layerGroup().addTo(this.mapInstance),
      uniques: L.layerGroup().addTo(this.mapInstance),
      parties: L.layerGroup().addTo(this.mapInstance),
      players: L.layerGroup().addTo(this.mapInstance),
    };

    // Add all markers
    this.addTownMarkers(L);
    this.addUniqueMarkers(L);
    this.addPartyMarkers(L);
    this.addPlayerMarkers(L);

    // Map click to show coordinates
    this.mapInstance.on('click', (e) => {
      this.setState({ selectedEntity: null });
    });

    this.setState({ mapReady: true });
  };

  addTownMarkers = (L) => {
    this.state.towns.forEach((town) => {
      const [lat, lng] = coordSROToMap(town.region, town.x, town.y);
      const icon = L.divIcon({
        className: 'sro-town-marker',
        html: `<div style="
          display:flex;align-items:center;justify-content:center;flex-direction:column;
          transform:translate(-50%,-50%);position:relative;cursor:pointer;
        ">
          <div style="
            width:32px;height:32px;border-radius:50%;border:2px solid #EAB308;
            background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;
            font-size:16px;box-shadow:0 0 12px rgba(234,179,8,0.4);
          ">🏰</div>
          <span style="
            background:rgba(0,0,0,0.85);color:#EAB308;font-size:10px;font-weight:700;
            padding:2px 6px;border-radius:4px;margin-top:2px;white-space:nowrap;
            border:1px solid rgba(234,179,8,0.5);letter-spacing:0.5px;
          ">${town.name}</span>
        </div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(this.markerLayers.towns);
      marker.on('click', () => {
        this.setState({
          selectedEntity: {
            type: 'Town',
            name: town.name,
            region: town.region,
            density: town.density,
            desc: town.desc,
            coords: `Region ${town.region} (X: ${town.x}, Y: ${town.y})`,
          }
        });
      });
    });
  };

  addUniqueMarkers = (L) => {
    this.state.uniques.forEach((mob) => {
      const [lat, lng] = coordSROToMap(mob.region, mob.x, mob.y);
      const isAlive = mob.status === 'ALIVE';
      const icon = L.divIcon({
        className: 'sro-unique-marker',
        html: isAlive ? `<div style="
          display:flex;align-items:center;justify-content:center;flex-direction:column;
          transform:translate(-50%,-50%);position:relative;cursor:pointer;
        ">
          <div style="
            position:absolute;width:44px;height:44px;border-radius:50%;
            background:rgba(236,72,153,0.25);border:2px solid rgba(217,70,239,0.8);
            animation:sroUniquePulse 1.5s infinite;
            box-shadow:0 0 20px rgba(217,70,239,0.6);
          "></div>
          <div style="
            width:24px;height:24px;border-radius:50%;background:#C026D3;
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 0 12px #E879F9;font-size:12px;position:relative;z-index:2;
          ">👑</div>
          <span style="
            background:#A21CAF;color:#fff;font-size:9px;font-weight:700;
            padding:2px 6px;border-radius:4px;margin-top:2px;white-space:nowrap;
            border:1px solid #F472B6;letter-spacing:0.3px;position:relative;z-index:2;
          ">${mob.name} (Lv.${mob.level})</span>
        </div>` : `<div style="
          display:flex;align-items:center;justify-content:center;flex-direction:column;
          transform:translate(-50%,-50%);cursor:pointer;opacity:0.5;
        ">
          <span style="
            background:rgba(0,0,0,0.7);color:#888;font-size:9px;
            padding:2px 6px;border-radius:4px;white-space:nowrap;
          ">☠️ ${mob.name} (Dead${mob.respawn ? ' · ' + mob.respawn : ''})</span>
        </div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(this.markerLayers.uniques);
      marker.on('click', () => {
        this.setState({
          selectedEntity: {
            type: 'Unique Monster',
            name: mob.name,
            level: mob.level,
            status: mob.status,
            hp: mob.hp,
            killer: mob.killer,
            respawn: mob.respawn,
            coords: `Region ${mob.region} (X: ${mob.x}, Y: ${mob.y})`,
          }
        });
      });
    });
  };

  addPartyMarkers = (L) => {
    this.state.parties.forEach((party) => {
      const [lat, lng] = coordSROToMap(party.region, party.x, party.y);
      const icon = L.divIcon({
        className: 'sro-party-marker',
        html: `<div style="
          display:flex;align-items:center;justify-content:center;flex-direction:column;
          transform:translate(-50%,-50%);cursor:pointer;
        ">
          <div style="
            width:14px;height:14px;border-radius:50%;background:#22C55E;
            border:2px solid #fff;box-shadow:0 0 8px #22C55E;
          "></div>
          <span style="
            background:rgba(0,0,0,0.85);color:#22C55E;font-size:9px;font-weight:600;
            padding:1px 5px;border-radius:3px;margin-top:1px;white-space:nowrap;
            border:1px solid rgba(34,197,94,0.4);
          ">👥 ${party.members}/8</span>
        </div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(this.markerLayers.parties);
      marker.on('click', () => {
        this.setState({
          selectedEntity: {
            type: 'EXP Party',
            name: party.name,
            master: party.master,
            members: party.members,
            build: party.build,
            area: party.area,
            coords: `Region ${party.region} (X: ${party.x}, Y: ${party.y})`,
          }
        });
      });
    });
  };

  addPlayerMarkers = (L) => {
    this.state.players.forEach((plr) => {
      const [lat, lng] = coordSROToMap(plr.region, plr.x, plr.y);
      const icon = L.divIcon({
        className: 'sro-player-marker',
        html: `<div style="
          display:flex;align-items:center;justify-content:center;flex-direction:column;
          transform:translate(-50%,-50%);cursor:pointer;
        ">
          <div style="
            width:10px;height:10px;border-radius:50%;background:#06B6D4;
            border:2px solid #fff;box-shadow:0 0 6px #06B6D4;
          "></div>
          <span style="
            background:rgba(0,0,0,0.85);color:#06B6D4;font-size:8px;font-weight:600;
            padding:1px 4px;border-radius:3px;margin-top:1px;white-space:nowrap;
            border:1px solid rgba(6,182,212,0.3);
          ">${plr.name}</span>
        </div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(this.markerLayers.players);
      marker.on('click', () => {
        this.setState({
          selectedEntity: {
            type: 'Player',
            name: plr.name,
            level: plr.level,
            build: plr.build,
            guild: plr.guild,
            state: plr.state,
            coords: `Region ${plr.region} (X: ${plr.x}, Y: ${plr.y})`,
          }
        });
      });
    });
  };

  toggleLayer = (layerKey, stateKey) => {
    this.setState((prev) => {
      const next = !prev[stateKey];
      if (this.mapInstance && this.markerLayers[layerKey]) {
        if (next) {
          this.mapInstance.addLayer(this.markerLayers[layerKey]);
        } else {
          this.mapInstance.removeLayer(this.markerLayers[layerKey]);
        }
      }
      return { [stateKey]: next };
    });
  };

  render() {
    const aliveUniques = this.state.uniques.filter((u) => u.status === 'ALIVE').length;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 70px)', width: '100%' }}>
        {/* CSS for unique pulse animation */}
        <style>{`
          @keyframes sroUniquePulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.6; }
          }
          .leaflet-container { background: #0a0e17 !important; }
          .leaflet-control-zoom a { background: #182026 !important; color: #06B6D4 !important; border-color: #394b59 !important; }
          .leaflet-control-zoom a:hover { background: #293742 !important; }
        `}</style>

        {/* Header Bar */}
        <div className="d-flex flex-wrap justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: '#182026', borderBottom: '1px solid #293742', flexShrink: 0 }}>
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <span className="font-weight-bold text-white" style={{ fontSize: '0.85rem', letterSpacing: '1px' }}>
              GOD MODE
            </span>
            <Tag intent={Intent.SUCCESS} round icon="dot" style={{ fontSize: '0.7rem' }}>
              3,142 ONLINE
            </Tag>
            <Tag intent={Intent.DANGER} round style={{ fontSize: '0.7rem' }}>
              {aliveUniques} UNIQUES ALIVE
            </Tag>
          </div>

          <ButtonGroup minimal style={{ gap: '2px' }}>
            <Button
              active={this.state.showPlayers}
              onClick={() => this.toggleLayer('players', 'showPlayers')}
              intent={this.state.showPlayers ? Intent.PRIMARY : Intent.NONE}
              icon="user" small
            >
              Players
            </Button>
            <Button
              active={this.state.showParties}
              onClick={() => this.toggleLayer('parties', 'showParties')}
              intent={this.state.showParties ? Intent.SUCCESS : Intent.NONE}
              icon="people" small
            >
              Parties
            </Button>
            <Button
              active={this.state.showUniques}
              onClick={() => this.toggleLayer('uniques', 'showUniques')}
              intent={this.state.showUniques ? Intent.DANGER : Intent.NONE}
              icon="flame" small
            >
              Uniques
            </Button>
            <Button
              active={this.state.showTowns}
              onClick={() => this.toggleLayer('towns', 'showTowns')}
              intent={this.state.showTowns ? Intent.WARNING : Intent.NONE}
              icon="home" small
            >
              Towns
            </Button>
          </ButtonGroup>
        </div>

        {/* Leaflet Map Container */}
        <div style={{ flex: 1, position: 'relative' }}>
          <div ref={this.mapRef} style={{ width: '100%', height: '100%' }} />

          {/* Entity Detail Panel */}
          {this.state.selectedEntity && (
            <div style={{
              position: 'absolute', bottom: 16, right: 16, width: 300, zIndex: 1000,
              backgroundColor: 'rgba(24, 32, 38, 0.95)', border: '1px solid #394b59',
              borderRadius: 6, padding: 14, color: '#fff', backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }}>
              <div className="d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #394b59', paddingBottom: 8, marginBottom: 8 }}>
                <strong style={{ color: '#06B6D4', fontSize: '0.85rem' }}>
                  {this.state.selectedEntity.type}: {this.state.selectedEntity.name}
                </strong>
                <Button minimal icon="cross" small onClick={() => this.setState({ selectedEntity: null })} style={{ color: '#fff' }} />
              </div>

              <div style={{ fontSize: '0.78rem', lineHeight: '1.6' }}>
                {this.state.selectedEntity.type === 'Unique Monster' && (
                  <>
                    <div>Status: <span style={{ color: this.state.selectedEntity.status === 'ALIVE' ? '#22C55E' : '#EF4444', fontWeight: 700 }}>{this.state.selectedEntity.status}</span></div>
                    <div>Level: <strong>Lv. {this.state.selectedEntity.level}</strong></div>
                    <div>Coords: <span style={{ color: '#EAB308', fontFamily: 'monospace' }}>{this.state.selectedEntity.coords}</span></div>
                    <div>HP: <span style={{ color: '#EAB308' }}>{this.state.selectedEntity.hp}</span></div>
                    {this.state.selectedEntity.killer && <div>Killed By: <span style={{ color: '#EF4444', fontWeight: 700 }}>{this.state.selectedEntity.killer}</span></div>}
                    {this.state.selectedEntity.respawn && <div>Respawn In: <span style={{ color: '#06B6D4' }}>{this.state.selectedEntity.respawn}</span></div>}
                  </>
                )}
                {this.state.selectedEntity.type === 'Player' && (
                  <>
                    <div>Level: <strong>Lv. {this.state.selectedEntity.level}</strong></div>
                    <div>Build: <span style={{ color: '#06B6D4' }}>{this.state.selectedEntity.build}</span></div>
                    <div>Guild: <span style={{ color: '#EAB308' }}>{this.state.selectedEntity.guild}</span></div>
                    <div>Action: <span style={{ color: '#22C55E' }}>{this.state.selectedEntity.state}</span></div>
                    <div>Coords: <span style={{ color: '#5c7080', fontFamily: 'monospace' }}>{this.state.selectedEntity.coords}</span></div>
                  </>
                )}
                {this.state.selectedEntity.type === 'EXP Party' && (
                  <>
                    <div>Leader: <strong style={{ color: '#06B6D4' }}>{this.state.selectedEntity.master}</strong></div>
                    <div>Members: <span style={{ color: '#22C55E', fontWeight: 700 }}>{this.state.selectedEntity.members}/8</span></div>
                    <div>Zone: <span style={{ color: '#EAB308' }}>{this.state.selectedEntity.area}</span></div>
                    <div>Builds: <span style={{ color: '#5c7080' }}>{this.state.selectedEntity.build}</span></div>
                  </>
                )}
                {this.state.selectedEntity.type === 'Town' && (
                  <>
                    <div>Coords: <span style={{ color: '#EAB308', fontFamily: 'monospace' }}>{this.state.selectedEntity.coords}</span></div>
                    <div>Population: <span style={{ color: '#EAB308', fontWeight: 700 }}>{this.state.selectedEntity.density.toLocaleString()} Players</span></div>
                    <div>Info: <span style={{ color: '#5c7080' }}>{this.state.selectedEntity.desc}</span></div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
